import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Phone,
  MapPin,
  ExternalLink,
  SkipForward,
} from "lucide-react";
import { Button, ErrorMessage, Field } from "./components.jsx";
import { fullAddress, googleDirections } from "../shared/deliveries.js";
import { PACKAGES } from "../shared/catalog.js";
import { api } from "./api.js";

export function RouteRunner({
  route: initialRoute,
  admin,
  admins,
  onChanged,
  onBack,
}) {
  const [route, setRoute] = useState(initialRoute),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = route.stops.filter((s) => s.status === "pending"),
    skipped = route.stops.filter((s) => s.status === "skipped"),
    done = route.stops.filter((s) => s.status === "completed"),
    stop = pending[0],
    own = route.assignedTo === admin.id;
  async function act(patch) {
    setBusy(true);
    setError("");
    try {
      const r = await api(`/api/admin/routes/${route.id}`, {
        method: "PATCH",
        body: { version: route.version, ...patch },
      });
      setRoute(r.route);
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="route-runner">
      <header>
        <button className="text-link" onClick={onBack}>
          <ArrowLeft size={16} /> All deliveries
        </button>
        <span>
          {done.length} of {route.stops.length} delivered
        </span>
      </header>
      <div className="route-runner-title">
        <span className="eyebrow">YOUR DOORSTEP DELIVERY ROUTE</span>
        <h2>{route.name}</h2>
        <div className="route-progress">
          <span
            style={{ width: `${(done.length / route.stops.length) * 100}%` }}
          />
        </div>
      </div>
      <ErrorMessage>{error}</ErrorMessage>
      {route.status === "completed" ? (
        <div className="route-finished">
          <CheckCircle2 size={48} />
          <h3>That’s a little magic, delivered.</h3>
          <p>
            {done.length} delivered.
            {skipped.length
              ? ` ${skipped.length} skipped orders are back in the ready list for another route.`
              : ""}
          </p>
          <Button onClick={onBack}>
            Back to deliveries <ArrowRight size={16} />
          </Button>
        </div>
      ) : (
        <>
          <div className="route-driver">
            <span>Delivering as</span>
            <select
              aria-label="Route driver"
              value={route.assignedTo}
              disabled={busy}
              onChange={(e) =>
                act({ action: "transfer", assignedTo: e.target.value })
              }
            >
              {admins.map((a) => (
                <option value={a.id} key={a.id}>
                  {a.name.split(" ")[0]}
                </option>
              ))}
            </select>
          </div>
          {!own ? (
            <div className="inline-notice">
              This route belongs to{" "}
              {
                admins
                  .find((a) => a.id === route.assignedTo)
                  ?.name.split(" ")[0]
              }
              . Change the driver above to pass it to yourself before
              delivering.
            </div>
          ) : stop ? (
            <article className="delivery-stop-card">
              <span className="eyebrow">
                STOP {stop.position + 1} · {stop.order.number}
              </span>
              <h3>{stop.order.customer.name}</h3>
              <a
                className="stop-address"
                href={googleDirections([stop.order])}
                target="_blank"
                rel="noreferrer"
              >
                <MapPin size={24} />
                <span>
                  {stop.order.customer.address}
                  {stop.order.customer.unit && `, ${stop.order.customer.unit}`}
                  <small>
                    {stop.order.customer.city}, ID {stop.order.customer.zip}
                  </small>
                </span>
                <ExternalLink size={18} />
              </a>
              <a
                className="stop-phone"
                href={`tel:${stop.order.customer.phone}`}
              >
                <Phone size={19} />
                {stop.order.customer.phone}
                <small>Call family</small>
              </a>
              {stop.order.customer.instructions && (
                <p className="stop-instructions">
                  {stop.order.customer.instructions}
                </p>
              )}
              <div className="stop-packages">
                {stop.order.items.map((i, n) => (
                  <div key={n}>
                    <strong>
                      {PACKAGES.find((p) => p.id === i.packageId).name}
                    </strong>
                    <span>{i.children.map((c) => c.name).join(", ")}</span>
                  </div>
                ))}
              </div>
              <a
                className="text-link maps-route-link"
                href={googleDirections(pending.slice(0, 4).map((s) => s.order))}
                target="_blank"
                rel="noreferrer"
              >
                Open next {Math.min(4, pending.length)}{" "}
                {pending.length === 1 ? "stop" : "stops"} in Google Maps{" "}
                <ExternalLink size={14} />
              </a>
              <div className="stop-actions">
                <Button
                  className="button-outline"
                  disabled={busy}
                  onClick={() => act({ action: "skip", orderId: stop.orderId })}
                >
                  <SkipForward size={18} /> Skip for now
                </Button>
                <Button
                  disabled={busy}
                  onClick={() =>
                    act({ action: "complete", orderId: stop.orderId })
                  }
                >
                  <CheckCircle2 size={18} />
                  {busy ? "Saving…" : "Complete & next"}
                </Button>
              </div>
              <p className="fine-print">
                Complete records delivery and queues the customer’s delivered
                email. Skip keeps the order ready.
              </p>
            </article>
          ) : (
            <div className="route-finished">
              <CheckCircle2 size={44} />
              <h3>You’ve visited every stop.</h3>
              <p>
                {skipped.length}{" "}
                {skipped.length === 1
                  ? "family still needs"
                  : "families still need"}{" "}
                a delivery.
              </p>
              <Button
                disabled={busy}
                onClick={() => act({ action: "revisit" })}
              >
                Revisit skipped stops
              </Button>
              <Button
                className="button-outline"
                disabled={busy}
                onClick={() => act({ action: "finish" })}
              >
                Finish route & return skipped orders
              </Button>
            </div>
          )}
        </>
      )}
      {skipped.length > 0 && route.status === "active" && (
        <p className="skipped-count">
          {skipped.length} skipped {skipped.length === 1 ? "stop" : "stops"} to
          revisit or return to the ready list.
        </p>
      )}
    </section>
  );
}
